import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faDoorOpen, faUsers } from "@fortawesome/free-solid-svg-icons";

import { BarraFiltros, BuscadorBarra, FiltroMulti } from "../../../../components/buscadorBarra/buscadorBarra";
import useEsTelefono from "../../../../utils/esTelefono";
import { AulaOcupacion, Clase, calendarioApi } from "./calendarioApi";
import { useGlobito } from "./globito";
import { DIAS_CORTOS, JORNADAS, JornadaKey, diaSemana, fechaCorta, fechaDe, iconoCurso, lunesDe, ocupaJornada, sumarDias, tituloClase, tramoClase, coincideCalendario } from "./formato";
import { Contexto, GloboClase, llaveClase } from "./piezas";
import "../../managment/enrollmentRequests/filtrosMatriculas.css";
import "./calendario.css";

/*
	Ocupación de Aulas (Esteban, 2026-11-01: «super importante», en Campus → Aulas y no en el Calendario).
	Por Día: cada aula con su cupo y lo que tiene en Mañana, Mediodía, Tarde y Noche, o «Libre». Por Semana: cuadrícula de
	7 días por 4 jornadas. «Solo Libres» y el cupo mínimo encuentran rápido dónde abrir un horario. La hora es la del nombre
	del horario (como en el Calendario); si el nombre no se puede leer, la de la sesión. Ratón encima = tarjetita de la clase;
	clic = el Calendario de ese día con su panel.
*/

type Modo = "dia" | "semana";
const CUPOS = [0, 15, 20, 25, 30];
const NOMBRE_JORNADA: { [k in JornadaKey]: string } = { M: "Mañana", D: "Mediodía", T: "Tarde", N: "Noche" };
const LIBRES_EN: { [k in JornadaKey]: string } = { M: "Libres en la Mañana", D: "Libres al Mediodía", T: "Libres en la Tarde", N: "Libres en la Noche" };

const hoyLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const porNombre = (a: AulaOcupacion, b: AulaOcupacion) => a.nombre.localeCompare(b.nombre, "es", { numeric: true, sensitivity: "base" });

const OcupacionAulas: React.FC<{ campusId: number; countryId: number | null; abreCalendario: boolean }> = ({ campusId, countryId, abreCalendario }) => {
	const navigate = useNavigate();
	// En el teléfono los días y las jornadas van cortos para que quepan en un renglón.
	const movil = useEsTelefono();
	const globito = useGlobito();
	const [modo, setModo] = useState<Modo>("dia");
	const [fecha, setFecha] = useState<string>(hoyLocal());
	const [jornada, setJornada] = useState<JornadaKey | "">("");
	const [aulas, setAulas] = useState<AulaOcupacion[] | null>(null);
	const [clases, setClases] = useState<Clase[]>([]);
	const [hoy, setHoy] = useState<string>(hoyLocal());
	const [error, setError] = useState<string | null>(null);
	const [q, setQ] = useState("");
	const [fEdificios, setFEdificios] = useState<number[]>([]);
	const [cupo, setCupo] = useState(0);
	const [soloLibres, setSoloLibres] = useState(false);
	const pedido = useRef(0);
	const lunes = lunesDe(fecha);

	useEffect(() => {
		if (!campusId) return;
		const n = ++pedido.current;
		calendarioApi.ocupacion(campusId, lunes)
			.then((r) => { if (n !== pedido.current) return; setAulas(r.data.aulas); setClases(r.data.clases); setHoy(r.data.hoy); setError(null); })
			.catch(() => { if (n !== pedido.current) return; setError("No se pudo cargar la ocupación."); setAulas((a) => a ?? []); });
	}, [campusId, lunes]);
	useEffect(() => { globito.cerrar(); }, [modo, fecha, jornada]); // eslint-disable-line react-hooks/exhaustive-deps

	// Clases por aula y fecha, ordenadas por hora.
	const porAula = useMemo(() => {
		const m = new Map<number, Clase[]>();
		clases.forEach((c) => { if (!c.aula) return; const l = m.get(c.aula.id) || []; l.push(c); m.set(c.aula.id, l); });
		m.forEach((l) => l.sort((a, b) => tramoClase(a)[0] - tramoClase(b)[0]));
		return m;
	}, [clases]);
	const de = (aulaId: number, f: string, k?: JornadaKey) => (porAula.get(aulaId) || []).filter((c) => c.fecha === f && (!k || ocupaJornada(c, k)));
	const libre = (a: AulaOcupacion) => (modo === "semana" ? (porAula.get(a.id) || []).length < 1 : de(a.id, fecha, jornada || undefined).length < 1);

	const filtradas = useMemo(() => (aulas || [])
		.filter((a) => (fEdificios.length < 1 || fEdificios.includes(a.edificio.id)) && a.cupo >= cupo && coincideCalendario(q, a.nombre, a.edificio.nombre)),
	[aulas, fEdificios, cupo, q]);
	const libres = filtradas.filter(libre).length;
	// Uso de la semana: jornadas ocupadas sobre las 28 posibles (7 días × 4 jornadas) de cada aula.
	const semanaFechas = Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i));
	const jornadasOcupadas = filtradas.reduce((s, a) => s + semanaFechas.reduce((t, f) => t + JORNADAS.filter((j) => de(a.id, f, j.key).length > 0).length, 0), 0);
	const uso = filtradas.length ? Math.round((jornadasOcupadas / (filtradas.length * 28)) * 100) : 0;
	const visibles = soloLibres ? filtradas.filter(libre) : filtradas;
	const edificios = Array.from(new Map(visibles.map((a) => [a.edificio.id, a.edificio.nombre])).entries()).sort((a, b) => a[1].localeCompare(b[1], "es"));
	const opcionesEdificio = useMemo(() => Array.from(new Map((aulas || []).map((a) => [a.edificio.id, a.edificio.nombre])).entries())
		.sort((a, b) => a[1].localeCompare(b[1], "es")).map(([id, name]) => ({ id, name })), [aulas]);

	// Clic = el Calendario de ese día con la clase abierta; quien no tiene Calendario solo ve la tarjetita.
	const ctx: Contexto = { campusId, countryId, globito, onAbrir: (c) => { if (abreCalendario) navigate(`/dailyView?fecha=${c.fecha}&horario=${c.horarioId}`); } };
	const hayFiltros = q !== "" || fEdificios.length > 0 || cupo > 0 || soloLibres;
	const etiquetaLibres = modo === "semana" ? "Sin Clases en la Semana" : jornada ? LIBRES_EN[jornada] : "Libres Todo el Día";

	const claseEnAula = (c: Clase) => {
		const d = globito.disparador("ocu" + llaveClase(c), () => <GloboClase c={c} ctx={ctx} />);
		return (
			<button type="button" key={llaveClase(c)} className="ocuClase" onMouseEnter={d.onMouseEnter} onMouseLeave={d.onMouseLeave} onClick={() => { globito.cerrar(); ctx.onAbrir(c); }}>
				<span className="i"><FontAwesomeIcon icon={iconoCurso(c)} /></span><span className="n">{tituloClase(c)}</span><span className="q">{c.activos}</span>
			</button>
		);
	};

	const tarjetaAula = (a: AulaOcupacion) => {
		const cab = <div className="ocuAulaCab"><b title={a.nombre}>{a.nombre}</b><span><FontAwesomeIcon icon={faUsers} /> {a.cupo || "—"}</span></div>;
		if (modo === "semana") {
			const dias = Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i));
			return (
				<div key={a.id} className={"ocuAula" + (libre(a) ? " libre" : "")}>
					{cab}
					<div className="ocuMini">
						<span />
						{dias.map((d) => <span key={d} className={"hd" + (d === hoy ? " hoy" : "")}>{DIAS_CORTOS[diaSemana(d)][0]}</span>)}
						{JORNADAS.map((j) => (
							<React.Fragment key={j.key}>
								<span className="lab">{j.nombre.slice(0, 3)}</span>
								{dias.map((d) => {
									const c = de(a.id, d, j.key)[0];
									if (!c) return <span key={d} className="c" />;
									const g = globito.disparador("ocu" + llaveClase(c) + j.key, () => <GloboClase c={c} ctx={ctx} />);
									return <button type="button" key={d} className="c o" aria-label={tituloClase(c)} onMouseEnter={g.onMouseEnter} onMouseLeave={g.onMouseLeave} onClick={() => { globito.cerrar(); ctx.onAbrir(c); }} />;
								})}
							</React.Fragment>
						))}
					</div>
				</div>
			);
		}
		const ks: JornadaKey[] = jornada ? [jornada] : ["M", "D", "T", "N"];
		const todoLibre = ks.every((k) => de(a.id, fecha, k).length < 1);
		return (
			<div key={a.id} className={"ocuAula" + (todoLibre ? (jornada ? " libreElegida" : " libre") : "")}>
				{cab}
				{ks.map((k) => {
					const cs = de(a.id, fecha, k);
					return (
						<div className="ocuFranja" key={k}>
							<small>{NOMBRE_JORNADA[k]}</small>
							<div>{cs.length ? cs.map(claseEnAula) : <span className={"ocuLibre" + (jornada ? " fuerte" : "")}><FontAwesomeIcon icon={faDoorOpen} />Libre</span>}</div>
						</div>
					);
				})}
			</div>
		);
	};

	const dias = Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i));
	return (
		<div className="calcCuerpo" style={{ padding: 0 }}>
			<div className="ocuBarra">
				<div className="calcSeg" role="group" aria-label="Vista">
					<button type="button" aria-pressed={modo === "dia"} onClick={() => setModo("dia")}>Día</button>
					<button type="button" aria-pressed={modo === "semana"} onClick={() => setModo("semana")}>Semana</button>
				</div>
				{/* Flechas y fechas juntas: en el teléfono bajan de renglón las tres, nunca una flecha sola. */}
				<span className="ocuSemanaNav">
					<button type="button" className="calcRedondo" aria-label="Semana anterior" onClick={() => setFecha(sumarDias(fecha, -7))}><FontAwesomeIcon icon={faChevronLeft} /></button>
					<span>{fechaCorta(lunes)} – {fechaCorta(sumarDias(lunes, 6))}</span>
					<button type="button" className="calcRedondo" aria-label="Semana siguiente" onClick={() => setFecha(sumarDias(fecha, 7))}><FontAwesomeIcon icon={faChevronRight} /></button>
				</span>
				{lunesDe(hoyLocal()) !== lunes ? <button type="button" className="calcHoy" onClick={() => setFecha(hoyLocal())}>Esta Semana</button> : null}
			</div>
			{modo === "dia" ? (
				<div className="ocuBarra">
					<div className="calcSeg chico" role="group" aria-label="Día">
						{dias.map((d) => <button type="button" key={d} aria-pressed={d === fecha} onClick={() => setFecha(d)}>{movil ? DIAS_CORTOS[diaSemana(d)][0] : DIAS_CORTOS[diaSemana(d)]} {fechaDe(d).getDate()}</button>)}
					</div>
					<div className="calcSeg chico" role="group" aria-label="Jornada">
						<button type="button" aria-pressed={jornada === ""} onClick={() => setJornada("")}>{movil ? "Todo" : "Todo el Día"}</button>
						{JORNADAS.map((j) => <button type="button" key={j.key} aria-pressed={jornada === j.key} onClick={() => setJornada(j.key)}>{j.nombre}</button>)}
					</div>
				</div>
			) : null}
			<BarraFiltros hayFiltros={hayFiltros} onLimpiar={() => { setQ(""); setFEdificios([]); setCupo(0); setSoloLibres(false); }}>
				<BuscadorBarra valor={q} onBuscar={setQ} placeholder="Buscar Aula" />
				<FiltroMulti titulo="Edificio" opciones={opcionesEdificio} elegidos={fEdificios} onChange={setFEdificios} />
				<select className="ocuSelect" aria-label="Cupo" value={cupo} onChange={(e) => setCupo(Number(e.target.value))}>
					{CUPOS.map((c) => <option key={c} value={c}>{c ? `Cupo ${c} o más` : "Cualquier Cupo"}</option>)}
				</select>
				<button type="button" className="ocuToggle" aria-pressed={soloLibres} onClick={() => setSoloLibres(!soloLibres)}><FontAwesomeIcon icon={faDoorOpen} />Solo Libres</button>
			</BarraFiltros>
			{error ? <div className="alert alert-danger" style={{ fontSize: 15, margin: 0 }}>{error}</div> : null}
			{aulas === null ? <div className="loader" /> : (
				<>
					<div className="calcCifras">
						<div className="calcCifra"><span>Aulas</span><b>{filtradas.length}</b></div>
						<div className="calcCifra"><span>{etiquetaLibres}</span><b>{libres}<small> de {filtradas.length}</small></b></div>
						<div className="calcCifra"><span>Ocupadas</span><b>{filtradas.length - libres}</b></div>
						<div className="calcCifra"><span>Uso de la Semana</span><b>{uso}%</b></div>
					</div>
					{visibles.length < 1 ? <div className="calcVacio">Ninguna aula con estos filtros.</div> : edificios.map(([id, nombre]) => {
						const as = visibles.filter((a) => a.edificio.id === id).sort(porNombre);
						return (
							<section className="ocuEdificio" key={id}>
								<div className="calcJornadaCab"><h5>{nombre}</h5><span>{as.length === 1 ? "1 aula" : `${as.length} aulas`} · {as.filter(libre).length} libres</span></div>
								<div className="ocuAulas">{as.map(tarjetaAula)}</div>
							</section>
						);
					})}
				</>
			)}
			{globito.capa}
		</div>
	);
};

export default OcupacionAulas;
